
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { UserRole } from '../../types';
import { ShieldCheck, Lock, ArrowRight } from 'lucide-react';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; auth?: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const validate = () => {
    const newErrors: { email?: string; password?: string } = {};
    if (!email) {
      newErrors.email = 'Terminal ID required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Invalid identity format';
    }
    if (!password) {
      newErrors.password = 'Security key missing';
    } else if (password.length < 3) {
      newErrors.password = 'Min key depth: 3';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    setErrors({});

    const response = await login(email, password);
    if (response.success && response.user) {
      const role = response.user.role.toUpperCase();
      navigate(role === UserRole.ADMIN ? '/admin/dashboard' : '/user/dashboard');
    } else if (!response.success && response.message) {
      setErrors({ auth: response.message });
    }
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* 1. Header Segment */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-primary/10 rounded-lg">
            <ShieldCheck className="text-brand-primary" size={20} />
          </div>
          <h2 className="text-2xl font-black text-white tracking-tighter uppercase italic">Access Terminal</h2>
        </div>
        <p className="text-[11px] text-brand-muted uppercase font-bold tracking-[0.2em] opacity-60">Authorize your agency session</p>
      </div>

      {/* 2. Error Segment */}
      {errors.auth && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-4">
          <div className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_12px_rgba(239,68,68,1)] animate-pulse"></div>
          <p className="text-red-400 text-[10px] font-black uppercase tracking-widest leading-none">{errors.auth}</p>
        </div>
      )}

      {/* 3. Staggered Inputs Segment */}
      <div className="space-y-5">
        <div className="">
          <Input
            label="Terminal Email ID"
            placeholder="identity@coinnexs.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            required
            autoComplete="email"
          />
        </div>
        <div className="">
          <Input
            label="Secure Hash Key"
            placeholder="••••••••"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            required
            autoComplete="current-password"
          />
        </div>
      </div>

      {/* 4. Controls Segment */}
      <div className="flex items-center justify-between px-2">
        <label className="flex items-center gap-3 cursor-pointer group">
          <div className="relative flex items-center justify-center">
            <input
              type="checkbox"
              className="peer appearance-none w-5 h-5 rounded-lg border border-brand-border bg-brand-dark/50 checked:bg-brand-primary checked:border-brand-primary transition-all duration-300"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <svg className="absolute w-3.5 h-3.5 text-black opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span className="text-[10px] font-black text-brand-muted uppercase tracking-widest group-hover:text-brand-text transition-colors">Trust Node</span>
        </label>
        <span
          onClick={() => navigate('/forgot-password')}
          className="text-[10px] text-brand-primary font-black uppercase tracking-widest hover:underline cursor-pointer opacity-70 hover:opacity-100 transition-all"
        >
          Key Recovery?
        </span>
      </div>

      {/* 5. CTA Segment */}
      <div className="pt-2">
        <Button
          type="submit"
          className="w-full h-16 relative overflow-hidden group/btn"
          isLoading={isLoading}
        >
          <div className="relative z-10 flex items-center gap-3">
            <span className="tracking-[0.4em]">
              {isLoading ? 'VALIDATING SIGNATURE...' : 'INITIATE ACCESS'}
            </span>
            {!isLoading && <ArrowRight size={18} className="group-hover/btn:translate-x-1 transition-transform" />}
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000 ease-in-out pointer-events-none"></div>
        </Button>
      </div>

      {/* 6. Footer Segment */}
      <div className="flex flex-col items-center gap-4">
        <p className="text-[10px] text-brand-muted font-bold uppercase tracking-[0.15em]">
          Unauthorized access is strictly monitored.
        </p>
        <div className="h-[1px] w-8 bg-brand-border/40"></div>
        <p className="text-[10px] font-black text-brand-muted uppercase tracking-widest">
          New Entity? <span className="text-brand-primary cursor-pointer hover:underline underline-offset-4" onClick={() => navigate('/register')}>Apply for Membership</span>
        </p>
      </div>
    </form>
  );
};

export default Login;
